import unicodeSubstring from 'unicode-substring';
/**
 * Returns an ellipsis of the text that can fit within the given width with a tail count.
 * @example
 * //returns 'abcdefghijkl…xyz'
 * ellipsis('abcdefghijklmnopqrstuvwxyz', 3, 120);
 * @example
 * //returns 'abcdefghijklmno…'
 * ellipsis('abcdefghijklmnopqrstuvwxyz', 0, 120);
 * @example
 * //returns 'abcdef…xyz'
 * ellipsis('abcdefghijklmnopqrstuvwxyz', 3, 120, 22, 400);
 * @param {String} text - The text used to make the ellipsis.
 * @param {Number} tailCount - The number of characters in the tail of text after `…`.
 * @param {Number} width - The width that the text should fit within.
 * @param {Number} [fontSize = 14] - The font size used to calculate the ellipsis.
 * @param {Number} [fontWeight = 400] - The font weight used to calculate the ellipsis.
 * @returns {String}
 */
export default (text, tailCount, width, fontSize = 14, fontWeight = 400) => {
  // server side rendering
  if (typeof document === 'undefined') return text;

  // If the text is shorter than the tailCount, no need to calculate anything at all and we can just return the text.
  // This also prevents miscalculation of `tail` and `rest` before the while loop which can cause an infinite loop
  if (text.length <= tailCount) {
    return text;
  }

  const ellipsisCharacter = '\u2026';
  const context = document.createElement('canvas').getContext('2d');
  context.font = `${fontWeight} ${fontSize}px Artifakt Element`;
  text = text.trim();
  const textWidth = context.measureText(text).width;
  const ellipsisWidth = context.measureText(ellipsisCharacter).width;

  // in case the text width can fit within the given size
  if (textWidth <= width) {
    return text;
  }

  // If we can't fit anything just put the first character and an ellipsis character.
  // This solves the case of an infinite loop when width is negative.
  if (width <= ellipsisWidth) {
    return `${text[0]}${ellipsisCharacter}`;
  }

  const tail = `${ellipsisCharacter}${unicodeSubstring(
    text,
    text.length - tailCount,
    text.length
  )}`;
  const rest = unicodeSubstring(text, 0, text.length - tailCount);

  let from = 0;
  let to = rest.length;
  let isFinished = false;
  let index;
  let guardCounter = text.length;

  // Binary search to find the best possible index that makes the resulting string fit within `width`.
  while (!isFinished && guardCounter) {
    index = Math.round((to - from) / 2 + from);
    // For future developers who want to make some performance improvements like these:
    // context.measureText(text.slice(0, index)).width + tailWidth (where we calculate tailWidth only once)
    // This might seem harmless at first glance,
    // but the width of a text will probably a floating point number.
    // And as an experienced Javascript developer you probably know by now that 0.1 + 0.2 !== 0.3
    // So when adding the tailWidth to a new calculated width this might result in a floating point conversion error.
    // By calculating the width of all characters together we avoid using operations on floating point numbers,
    // thus making the comparison to the requested width more accurate.
    // So although it might be slower to recalculated the width of the same string over and over again,
    // it must be done to achieve better precision.
    const currentIndexWidth = context.measureText(unicodeSubstring(text, 0, index) + tail).width;
    const currentIndexPlusOneWidth = context.measureText(
      unicodeSubstring(text, 0, index + 1) + tail
    ).width;

    if (currentIndexWidth > width) {
      to = index;
    } else {
      from = index;
    }

    isFinished = (currentIndexWidth <= width && currentIndexPlusOneWidth > width) || index === 1;
    guardCounter--;
  }

  // Guarding just in case to prevent an infinite loop. Better overflowing text than stuck app.
  if (!guardCounter) {
    return text;
  }

  return `${unicodeSubstring(text, 0, index)}${tail}`;
};
